/**
 * Stacking order for everything voice-related.
 *
 * The app's drawers are `react-modern-drawer`, which renders its overlay at
 * z-index 100 and its own panel at 101 — well above the `z-50` most floating UI
 * uses. Anything voice-related opened from inside a drawer (the inbox Details
 * panel is the one that matters) therefore lands *under* that overlay: dimmed by
 * its backdrop, and every click on it hits the overlay instead, which closes the
 * drawer and unmounts whatever was inside it.
 */

/** Pre-dial modal — must clear the drawer panel (101). */
export const PRE_DIAL_MODAL_Z_INDEX = 102;

/**
 * The live-call card, pill and disposition dialog. Deliberately above the
 * pre-dial modal as well: hanging up must never be covered by another layer.
 * (The mic/reconnect toasts go through the global ToastContainer, which
 * stacks above everything on its own.)
 */
export const SOFTPHONE_OVERLAY_Z_CLASS = "z-[110]";

/**
 * The in-call Create-lead modal (CRM-905) — opened from the live-call card, so
 * it must clear the softphone overlay itself.
 */
export const IN_CALL_LEAD_MODAL_Z_INDEX = 120;

/**
 * The message pop-up (CRM-1133) — a floating card / right-hand drawer any page
 * can open, which then stays up across navigation.
 *
 * It is a PERSISTENT page-level layer, not a modal one: above the page chrome
 * (the aside nav is z-[14], the onboarding checklist z-[12]) but BELOW every
 * transient modal layer — `Modal` (50), react-modern-drawer (100/101), the
 * softphone (110). A drawer or modal the user opens later (Edit view on Group
 * metrics, Notes, a confirm dialog) has to land on top of the pop-up, and its
 * own nested menus and dialogs have to stack the way they do everywhere else.
 * The original z-[105] put the pop-up above the drawers, which hid every
 * drawer opened while it was up behind the pop-up itself.
 */
export const MESSAGE_POPUP_Z_CLASS = "z-[45]";

/**
 * Layers the pop-up portals to <body>: the Settings popover, the Change-layout
 * menu, the applicant dropdown, the AI-active tooltip. `FloatingPortal` appends
 * them to <body> with `z-index: auto`, which loses to the positioned card, so
 * each one needs an explicit class or it renders *behind* the pop-up. `z-50`
 * is the app's floating-UI default, so they stack like any other popover.
 */
export const MESSAGE_POPUP_MENU_Z_CLASS = "z-50";

/**
 * Modals opened from inside the pop-up (Invite to apply, the no-subject and
 * template-replace confirms). `Modal`'s default 50 already clears the pop-up;
 * the constant stays so the contract lives in one place. Do NOT raise it: the
 * Invite modal's select menus (menuPortal z 50/52) and nested dialogs (Active
 * discount codes, z 50) only stack above it by DOM order while it sits at the
 * default — at 107 they all rendered underneath it.
 */
export const MESSAGE_POPUP_MODAL_Z_INDEX = 50;
